// saft_price_controller.js
//
// The /priskalkulator page: read a SAF-T file in the browser, show what a year with us would
// cost, and -- when we recognise the system it came from -- what the same year costs there.
//
// The file never leaves the browser. It is read by classes/saft/saft_reader.js, which keeps
// nothing but a handful of counts, and nothing on this page makes a request with them. Anything
// that came out of the file reaches the page through textContent, never innerHTML, since a
// company name in a SAF-T header is text somebody else typed.
import { Controller } from '@hotwired/stimulus'
import { readSaft, SaftError, SOFTWARE_BILLER } from '../classes/saft/saft_reader'
import { ZipError } from '../classes/saft/zip_reader'
import {
  billers,
  comparison,
  competitorPrices,
  detectVendor,
  employeeEstimate,
  hasPriceList,
  kr,
  ownPrice,
  PAYROLL_PER_EMPLOYEE,
  SERVICE_NAMES,
  usageFromSummary,
} from '../classes/saft/price_estimate'

const dateFormat = new Intl.DateTimeFormat('nb-NO', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
const monthName = new Intl.DateTimeFormat('nb-NO', { month: 'long', timeZone: 'UTC' })
const monthYear = new Intl.DateTimeFormat('nb-NO', { month: 'long', year: 'numeric', timeZone: 'UTC' })
const shortDate = new Intl.DateTimeFormat('nb-NO', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'UTC',
})

export default class extends Controller {
  static targets = [
    'upload',
    'dropzone',
    'input',
    'progress',
    'progressBar',
    'error',
    'errorMessage',
    'result',
    'company',
    'source',
    'period',
    'savingBox',
    'savingLead',
    'savingAmount',
    'savingText',
    'unknownBox',
    'unknownText',
    'field',
    'ownName',
    'ownTotal',
    'ownMonthly',
    'ownLines',
    'theirCard',
    'billedBox',
    'billedAmount',
    'billedDetail',
    'estimate',
    'theirName',
    'theirTotal',
    'theirMonthly',
    'theirLines',
    'theirSource',
    'plan',
    'notes',
  ]

  static values = { data: Object }

  // ---- Reading the file ----

  choose() {
    const [file] = this.inputTarget.files
    if (file) this.read(file)
  }

  dragOver(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add('border-pink-luca', 'bg-brand-tint')
  }

  dragLeave() {
    this.dropzoneTarget.classList.remove('border-pink-luca', 'bg-brand-tint')
  }

  drop(event) {
    event.preventDefault()
    this.dragLeave()
    const [file] = event.dataTransfer.files
    if (file) this.read(file)
  }

  async read(file) {
    this.errorTarget.hidden = true
    this.uploadTarget.hidden = true
    this.resultTarget.hidden = true
    this.progressTarget.hidden = false
    this.progressBarTarget.style.width = '0%'

    try {
      this.summary = await readSaft(file, {
        onProgress: (fraction) => (this.progressBarTarget.style.width = `${Math.round(fraction * 100)}%`),
        billers: billers(this.dataValue.vendors),
      })
    } catch (error) {
      this.fail(error)
      return
    }

    this.progressTarget.hidden = true
    this.start()
  }

  fail(error) {
    const known = error instanceof SaftError || error instanceof ZipError
    if (!known) console.error(error)
    this.errorMessageTarget.textContent = known
      ? error.message
      : 'Filen kunne ikke leses. Sjekk at det er en SAF-T-fil (XML) eller en zip med en SAF-T-fil i.'
    this.progressTarget.hidden = true
    this.errorTarget.hidden = false
    this.uploadTarget.hidden = false
    this.inputTarget.value = ''
  }

  reset() {
    this.summary = null
    this.inputTarget.value = ''
    this.resultTarget.hidden = true
    this.errorTarget.hidden = true
    this.uploadTarget.hidden = false
  }

  ignore(event) {
    event.preventDefault()
  }

  // ---- What the file told us ----

  start() {
    const summary = this.summary
    const { vendors } = this.dataValue

    this.vendorKey = detectVendor(vendors, summary)
    // Our own export names the app it came from (Saft::Export::AuditFile writes the instance's
    // application name), so a Numo file on the Numo site is a customer we already have.
    const written = `${summary.softwareCompanyName} ${summary.softwareId}`.toLowerCase()
    this.alreadyOurs = written.includes(this.dataValue.application_name.toLowerCase())
    this.fill(usageFromSummary(summary))

    this.companyTarget.textContent = summary.companyName || 'Regnskapet ditt'
    this.sourceTarget.textContent = `Ført i ${this.systemName()}${this.periodText()}`
    this.periodTarget.textContent = this.scaleText()

    this.planTarget.replaceChildren()
    this.resultTarget.hidden = false
    this.update()
    this.resultTarget.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  systemName() {
    if (this.vendorKey) return this.dataValue.vendors[this.vendorKey].navn
    return this.summary.softwareId || this.summary.softwareCompanyName || 'et ukjent regnskapsprogram'
  }

  periodText() {
    const { selectionStart, selectionEnd } = this.summary
    if (!selectionStart || !selectionEnd) return ''
    return `, ${dateFormat.format(selectionStart)} til ${dateFormat.format(selectionEnd)}`
  }

  scaleText() {
    const months = this.summary.months
    if (!months) return 'Filen sier ikke hvilken periode den gjelder, så tallene er tatt som de står.'
    if (Math.abs(months - 12) < 0.5) return 'Filen dekker et helt år.'
    return `Filen dekker ${kr(months)} måneder, så tallene er regnet om til et helt år.`
  }

  // ---- The usage form ----

  fill(usage) {
    for (const field of this.fieldTargets) {
      const value = usage[field.dataset.field]
      if (field.type === 'checkbox') field.checked = value
      else field.value = value
    }
  }

  usage() {
    const usage = {}
    for (const field of this.fieldTargets) {
      const key = field.dataset.field
      if (field.type === 'checkbox') usage[key] = field.checked
      else if (field.tagName === 'SELECT') usage[key] = field.value
      else usage[key] = Math.max(0, Math.round(Number(field.value) || 0))
    }
    return usage
  }

  // A changed number can change which plan is cheapest, so the plan is chosen afresh.
  update(event) {
    // Holding includes bank integration in our price, so choosing it switches the bank on --
    // otherwise the competitor would be quoted without a bank module we hand out for free.
    const field = event?.target
    if (field?.dataset.field === 'form' && field.value === 'holding') {
      this.fieldTargets.find((target) => target.dataset.field === 'bank').checked = true
    }
    this.planTarget.replaceChildren()
    this.render()
  }

  // ---- The prices ----

  render() {
    const usage = this.usage()
    const { prices, vendors, application_name: applicationName } = this.dataValue

    const own = ownPrice(prices, usage)
    this.ownNameTarget.textContent = applicationName
    this.ownTotalTarget.textContent = kr(own.total)
    this.ownMonthlyTarget.textContent = `Ca. ${kr(Math.round(own.total / 12))} kr i måneden`
    this.lines(this.ownLinesTarget, own.lines)

    const notes = [
      `Prisene er eks. mva. Hos oss koster ingen bilag eller fakturaer noe ekstra, uansett hvor mange du har. ` +
        `Grunnprisen gjelder årlig betaling; betaler du månedlig, er den ${prices.maned} kr i måneden.`,
      'Antall bilag er antall bilag i filen. Fakturaer til og fra selskaper med organisasjonsnummer er regnet ' +
        'som EHF, og en regning er regnet som betalt fra banken når leverandørgjelden er gjort opp mot en bankkonto.',
    ]
    if (usage.employees > 0) notes.push(this.employeeNote())

    const vendor = this.alreadyOurs ? null : this.vendorKey ? vendors[this.vendorKey] : this.unknownVendor()
    if (!vendor) {
      this.renderWithoutComparison(applicationName)
      this.list(this.notesTarget, notes)
      return
    }

    if (!hasPriceList(vendor)) {
      this.renderBilledOnly(vendor, own, notes, applicationName)
      return
    }

    const plans = competitorPrices(vendor, usage)
    if (this.planTarget.options.length === 0) this.planOptions(vendor, plans)
    const theirs = plans.find((plan) => plan.key === this.planTarget.value) || plans[0]
    // The invoices in the file only win when they come to at least the list price. Less than
    // that usually means some of them were booked elsewhere or paid by someone else, and a low
    // figure would understate the saving -- the list price is the fairer comparison then.
    const found = this.billed()
    const billed = found && found.perYear >= theirs.total ? found : null
    const compared = comparison(own, theirs, billed?.perYear ?? null)

    this.theirCardTarget.hidden = false
    this.estimateTarget.hidden = false
    this.showBilled(vendor, billed)
    this.theirNameTarget.textContent = vendor.navn
    this.theirTotalTarget.textContent = kr(theirs.total)
    this.theirMonthlyTarget.textContent = `Ca. ${kr(Math.round(theirs.total / 12))} kr i måneden`
    this.lines(this.theirLinesTarget, theirs.lines)
    this.theirSourceTarget.textContent = this.sourceText(vendor)

    this.unknownBoxTarget.hidden = true
    this.savingBoxTarget.hidden = false
    this.renderSaving(compared, billed ? vendor.navn : theirs.navn, applicationName)

    if (billed) {
      notes.push(...this.billedNotes(vendor, billed))
    } else {
      notes.push(`Prisen hos ${vendor.navn} er et anslag ut fra listeprisene deres.`)
    }

    if (theirs.key === plans[0].key) {
      notes.push(
        `Vi vet ikke hvilken pakke du har hos ${vendor.navn}, så vi har valgt den billigste av dem som passer best ` +
          'til bruken din. Har du en annen, kan du velge den over.',
      )
    }
    if (theirs.blockers.length > 0) {
      notes.push(`${theirs.navn} ${joinList(theirs.blockers)}, så pakken dekker ikke hele behovet ditt.`)
    }
    const excluded = compared.excluded.map((key) => SERVICE_NAMES[key])
    const unpriced = compared.missing.filter((key) => !compared.excluded.includes(key)).map((key) => SERVICE_NAMES[key])
    if (excluded.length > 0) {
      notes.push(
        `${theirs.navn} har ikke ${joinList(excluded)}. Det må i så fall kjøpes et annet sted, så vi har holdt det ` +
          'utenfor begge prisene i sammenligningen.',
      )
    }
    if (unpriced.length > 0) {
      notes.push(
        `${theirs.navn} har ikke ${joinList(unpriced)}. Det må i så fall kjøpes et annet sted, og er ikke regnet med ` +
          'i prisen deres.',
      )
    }
    notes.push(...theirs.notes)
    this.list(this.notesTarget, notes)
  }

  // A vendor with no price list we can count on -- Uni Micro, or a system we know nothing
  // about -- is compared on its invoices in the file alone. Without them there is nothing to
  // put next to our price.
  renderBilledOnly(vendor, own, notes, applicationName) {
    const billed = this.billed()
    if (!billed) {
      this.renderWithoutComparison(applicationName)
      this.list(this.notesTarget, notes)
      return
    }

    // We cannot tell which of our lines their invoices cover, so our side keeps all of them.
    const compared = comparison(own, { lines: [], total: 0 }, billed.perYear)

    this.theirCardTarget.hidden = false
    this.estimateTarget.hidden = true
    this.showBilled(vendor, billed)
    this.theirNameTarget.textContent = vendor.navn
    this.theirSourceTarget.textContent = `Vi har ikke prisene til ${vendor.navn}, så her står bare det filen viser at du er fakturert.`

    this.unknownBoxTarget.hidden = true
    this.savingBoxTarget.hidden = false
    this.renderSaving(compared, vendor.navn, applicationName)

    notes.push(
      ...this.billedNotes(vendor, billed),
      `Vi vet ikke hva fakturaene fra ${vendor.navn} dekker, så prisen vår har med alt i listen over, ` +
        'også det de kanskje ikke tar betalt for.',
    )
    this.list(this.notesTarget, notes)
  }

  showBilled(vendor, billed) {
    this.billedBoxTarget.hidden = !billed
    if (!billed) return
    this.billedAmountTarget.textContent = kr(billed.perYear)
    if (billed.monthly && billed.scaled) {
      this.billedDetailTarget.textContent =
        `${kr(billed.vouchers)} bilag fra ${vendor.navn}, ${kr(billed.amount)} kroner for ` +
        `${monthSpan(billed.monthly)}, regnet om til et helt år.`
    } else {
      this.billedDetailTarget.textContent = billed.scaled
        ? `${kr(billed.vouchers)} bilag fra ${vendor.navn}, ${kr(billed.amount)} kroner for perioden i filen, regnet om til et helt år.`
        : `${kr(billed.vouchers)} bilag fra ${vendor.navn} i filen.`
    }
  }

  billedNotes(vendor, billed) {
    const notes = [
      `Det ${vendor.navn} har fakturert er summen av kostnadene på bilagene fra dem i filen, eks. mva. Det er ` +
        'det sammenligningen bruker. Det kan inneholde ting vi ikke har regnet med, som ekstra brukere og ' +
        'tilleggstjenester, eller et årsoppgjør fakturert året etter det gjelder.',
    ]
    if (billed.monthly && billed.scaled) {
      notes.push(
        `${vendor.navn} fakturerer deg hver måned, og fakturaene i filen dekker ${monthSpan(billed.monthly)}. ` +
          'Det årlige beløpet er snittet av dem ganger tolv.',
      )
      return notes
    }
    // Otherwise the yearly figure is spread over the whole file. A customer who moved to the
    // vendor part-way through has paid them for fewer months than that, so the figure reads low.
    const start = this.summary.selectionStart || this.summary.firstDate
    if (billed.firstDate && start && billed.firstDate - start > 45 * 86400000) {
      notes.push(
        `Den første fakturaen fra ${vendor.navn} i filen er datert ${dateFormat.format(billed.firstDate)}. ` +
          `Har du ikke betalt dem for hele perioden filen dekker, er det årlige beløpet regnet for lavt.`,
      )
    }
    return notes
  }

  employeeNote() {
    const { perMonth } = employeeEstimate(this.summary)
    if (perMonth === null)
      return 'Antall ansatte er anslått fra lønnsføringene i filen. Juster det om det ikke stemmer.'
    return (
      `Filen sier ikke hvem som får lønn, så antall ansatte er anslått ut fra lønnskostnadene: rundt ` +
      `${kr(Math.round(perMonth / 1000) * 1000)} kroner i måneden, regnet som ${kr(PAYROLL_PER_EMPLOYEE)} kroner ` +
      'per ansatt. Juster det om det ikke stemmer.'
    )
  }

  // A system we have no data on at all is compared on its invoices in the file, like a vendor
  // with no price list, when the books have any from the company that wrote the file.
  unknownVendor() {
    return this.billed() ? { navn: this.systemName(), pakker: {} } : null
  }

  renderWithoutComparison(applicationName) {
    this.theirCardTarget.hidden = true
    this.savingBoxTarget.hidden = true
    this.unknownBoxTarget.hidden = false
    this.unknownTextTarget.textContent = this.alreadyOurs
      ? `Denne filen er laget i ${applicationName}, så du er allerede hos oss. Her er hva bruken din koster.`
      : `Vi har ikke prisene til ${this.systemName()}, så vi kan ikke si hva du betaler i dag. ` +
        `Her er hva det samme regnskapet koster hos ${applicationName}.`
  }

  // What the file says the vendor charged in a year, or null when it has no voucher from them.
  billed() {
    const billed = this.summary.billed?.[this.vendorKey || SOFTWARE_BILLER]
    if (!billed || billed.amount <= 0) return null

    // Three or more bills in a row, a month apart, are a monthly subscription: a year of it is
    // the average bill times twelve, however much of the file came before the first of them.
    // A customer who moved to the vendor part-way through -- or was billed through their
    // accountant until then -- would otherwise have a few months' bills spread over the year.
    const monthly = monthlyRun(billed.months)
    if (monthly) {
      return {
        amount: Math.round(billed.amount),
        perYear: Math.round((billed.amount * 12) / monthly.length),
        vouchers: billed.vouchers,
        firstDate: billed.firstDate,
        monthly,
        scaled: monthly.length !== 12,
      }
    }

    const months = this.summary.months
    const scaled = Boolean(months) && Math.abs(months - 12) >= 0.5
    return {
      amount: Math.round(billed.amount),
      perYear: Math.round(scaled ? (billed.amount * 12) / months : billed.amount),
      vouchers: billed.vouchers,
      firstDate: billed.firstDate,
      monthly: null,
      scaled,
    }
  }

  renderSaving(compared, name, applicationName) {
    const { saving } = compared
    const costs = compared.billed
      ? `${name} har fakturert deg ${kr(compared.theirs)} kroner eks. mva. i året, ifølge bilagene i filen.`
      : `${name} koster deg rundt ${kr(compared.theirs)} kroner i året.`
    const left = compared.excluded.map((key) => SERVICE_NAMES[key])
    const leftOut = left.length > 0 ? ` ${capitalize(joinList(left))} er ikke med, fordi de ikke tilbyr det.` : ''

    if (saving > 0) {
      this.savingBoxTarget.classList.replace('bg-surface-tint', 'bg-positive-surface')
      this.savingLeadTarget.textContent = `Bytter du fra ${name}, sparer du${compared.billed ? '' : ' anslagsvis'}`
      this.savingAmountTarget.textContent = `${kr(saving)} kr i året`
      this.savingTextTarget.textContent = `${costs} Det samme regnskapet koster ${kr(compared.own)} kroner hos ${applicationName}.${leftOut}`
    } else {
      this.savingBoxTarget.classList.replace('bg-positive-surface', 'bg-surface-tint')
      this.savingLeadTarget.textContent = `Med tallene dine koster ${name} omtrent`
      this.savingAmountTarget.textContent = `${kr(-saving)} kr mindre i året`
      this.savingTextTarget.textContent =
        `${costs} Hos ${applicationName} koster det samme regnskapet ${kr(compared.own)} kroner.${leftOut} ` +
        'Hos oss koster ingen bilag eller fakturaer ekstra, så prisen står stille om du vokser.'
    }
  }

  // Listed in the vendor's own order, cheapest to dearest as their price page has them, with the
  // likeliest plan selected.
  planOptions(vendor, plans) {
    for (const key of Object.keys(vendor.pakker)) {
      const plan = plans.find((candidate) => candidate.key === key)
      const option = document.createElement('option')
      option.value = plan.key
      option.textContent =
        plan.blockers.length > 0 || plan.impossible ? `${plan.navn} (dekker ikke behovet)` : plan.navn
      this.planTarget.append(option)
    }
    this.planTarget.value = plans[0].key
  }

  sourceText(vendor) {
    const checked = vendor.sjekket ? ` ${shortDate.format(new Date(vendor.sjekket))}` : ''
    const source = vendor.kilde ? ` fra ${vendor.kilde.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}` : ''
    return `Listepriser${source}, hentet${checked}. Anslaget er basert på tallene i filen din.`
  }

  lines(target, lines) {
    target.replaceChildren(
      ...lines.map((line) => {
        const item = document.createElement('li')
        item.className = 'flex justify-between gap-4'
        const label = document.createElement('span')
        label.textContent = line.label
        const amount = document.createElement('span')
        amount.className = 'whitespace-nowrap font-semibold text-content'
        amount.textContent = line.amount === null ? '–' : `${kr(line.amount)} kr`
        item.append(label, amount)
        return item
      }),
    )
  }

  list(target, items) {
    target.replaceChildren(
      ...items.map((text) => {
        const item = document.createElement('li')
        item.textContent = text
        return item
      }),
    )
  }
}

// The months a vendor billed in, when they are three or more consecutive months -- "2026-07"
// through "2026-10" -- or null when the bills are not monthly.
function monthlyRun(months) {
  if (!months || months.length < 3) return null
  const index = (month) => Number(month.slice(0, 4)) * 12 + Number(month.slice(5, 7))
  const sorted = [...months].sort()
  const consecutive = sorted.every((month, i) => i === 0 || index(month) - index(sorted[i - 1]) === 1)
  return consecutive ? sorted : null
}

// "juli til oktober 2026", or "desember 2025 til mars 2026" across a new year.
function monthSpan(months) {
  const date = (month) => new Date(`${month}-01T00:00:00Z`)
  const first = date(months[0])
  const last = date(months[months.length - 1])
  const sameYear = first.getUTCFullYear() === last.getUTCFullYear()
  return `${(sameYear ? monthName : monthYear).format(first)} til ${monthYear.format(last)}`
}

function capitalize(text) {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

function joinList(items) {
  if (items.length < 2) return items.join('')
  return `${items.slice(0, -1).join(', ')} og ${items[items.length - 1]}`
}
